import React, { useEffect, useState } from 'react';
import { Alert, Button, TextField } from '@mui/material';
import { Check, Copy, Gift } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { claimReferral, getReferralMe, type ReferralMe } from '../../services/referral.api';

/** Invite link, friend counts and a box for the user's own referral code (only during a campaign). */
export const InviteFriendsCard: React.FC = () => {
  const { t } = useTranslation();
  const [me, setMe] = useState<ReferralMe | null>(null);
  const [copied, setCopied] = useState(false);
  const [code, setCode] = useState('');
  const [status, setStatus] = useState<'idle' | 'busy' | 'ok' | 'failed'>('idle');

  useEffect(() => {
    void getReferralMe().then((res) => {
      if (res.success && res.data) setMe(res.data);
    });
  }, []);

  if (!me || (!me.hasActiveCampaign && me.registeredCount === 0)) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(me.link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard blocked: the link remains selectable.
    }
  };

  const apply = async () => {
    setStatus('busy');
    const res = await claimReferral(code.trim());
    if (res.success && res.data?.claimed) {
      setStatus('ok');
      setMe({ ...me, alreadyReferred: true });
    } else {
      setStatus('failed');
    }
  };

  return (
    <div className="mb-12 rounded-xl border border-gray-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-900">
      <h2 className="mb-1 flex items-center gap-2 text-xl font-bold text-gray-900 dark:text-slate-100">
        <Gift size={20} />
        {t('referral.card.title', 'Invite friends')}
      </h2>
      <p className="mb-4 text-sm text-gray-600 dark:text-slate-400">
        {t(
          'referral.card.body',
          'Share your link. When friends join with it, you both can earn rewards.'
        )}
      </p>
      <div className="flex gap-2">
        <TextField fullWidth size="small" value={me.link} InputProps={{ readOnly: true }} />
        <Button
          variant="outlined"
          onClick={() => void copy()}
          startIcon={copied ? <Check size={16} /> : <Copy size={16} />}
          className="shrink-0 normal-case"
        >
          {copied ? t('referral.card.copied', 'Copied') : t('referral.card.copy', 'Copy')}
        </Button>
      </div>
      <p className="mt-3 text-sm text-gray-600 dark:text-slate-400">
        {t('referral.card.stats', '{{registered}} friends joined · {{qualified}} made a purchase', {
          registered: me.registeredCount,
          qualified: me.qualifiedCount,
        })}
      </p>
      {!me.alreadyReferred && me.hasActiveCampaign && (
        <div className="mt-4 border-t border-gray-100 pt-4 dark:border-slate-800">
          <p className="mb-2 text-sm font-medium text-gray-800 dark:text-slate-200">
            {t('referral.card.haveCode', 'Were you invited? Enter your friend’s code')}
          </p>
          <div className="flex gap-2">
            <TextField
              size="small"
              value={code}
              placeholder={t('referral.prompt.placeholder', 'Referral code')}
              inputProps={{ maxLength: 16 }}
              onChange={(event) => setCode(event.target.value.toUpperCase())}
            />
            <Button
              variant="contained"
              className="normal-case"
              disabled={status === 'busy' || code.trim().length < 4}
              onClick={() => void apply()}
            >
              {t('referral.prompt.apply', 'Apply')}
            </Button>
          </div>
          {status === 'failed' && (
            <Alert severity="warning" className="mt-2">
              {t('referral.prompt.failed', 'This code cannot be applied.')}
            </Alert>
          )}
        </div>
      )}
      {status === 'ok' && (
        <Alert severity="success" className="mt-3">
          {t('referral.card.applied', 'Referral code applied.')}
        </Alert>
      )}
    </div>
  );
};
